/**
 * Die Bewegung der **Auswahl-Hülle** (#293): eine Fläche hinter markierten Nachbarzeilen, die beim
 * Dazunehmen und Abwählen wächst, schrumpft, sich teilt und wieder verschmilzt.
 *
 * **Die Werte sind gewählt, nicht gerechnet:** Variante X im Prototyp
 * `tools/selection-merge-prototype` (Sven, 08.10.2026). 260ms auf einer Kurve, die früh fast
 * ankommt und dann ausläuft, damit ein Shift-Klick über fünf Zeilen nicht nachschleppt.
 *
 * **Schrift und Haken der Zeilen laufen im selben Takt**, per CSS: Dieselben Zahlen stehen in
 * `SelectionHull/_selectionHullMotion.scss`, ein Test hält beide gleich. Sonst sprang die Schrift
 * um, während die Fläche noch fuhr (gemessen 08.10.2026, Bild für Bild).
 *
 * **Fahren nur, wenn sich die Auswahl ändert.** Ändert sich nur das Layout (eine Zeile klappt auf,
 * eine neue fährt ein, das Fenster wird breiter), steht die Hülle sofort am neuen Ort. Eine Fläche,
 * die einer aufklappenden Karte hinterherfährt, läse sich als Nachlauf.
 *
 * Bewusst ohne DOM-Bezug: Hier wird gerechnet, in der Hülle eingesetzt, und geprüft ohne
 * gerendertes Bauteil.
 */

import type { Transition } from 'framer-motion'

/** Dauer in Sekunden. */
export const SELECTION_HULL_DURATION = 0.26
/**
 * Dauer eines Schritts per Pfeiltaste (↑/↓, auch gehalten, auch mit Shift), in Sekunden. Mit 260ms lief
 * die Fläche beim Durchwandern hinterher; Klick, Shift-Klick und ⌘-Klick bleiben bei 260ms (Sven,
 * 09.10.2026, `tools/arrow-step-motion-prototype`).
 */
export const SELECTION_HULL_KEY_DURATION = 0.16
/** Kurve: früh fast da, dann ein langes Auslaufen. Dieselbe fährt dnd-kit beim Umsortieren. */
export const SELECTION_HULL_EASE = [0.25, 1, 0.5, 1] as const
/**
 * Verzug je Rang, mit dem Schrift und Haken **dazukommender** Zeilen nachziehen (Sekunden).
 *
 * Nur beim Dazukommen: Wer geht, geht im Takt der Fläche. Beim Abwählen hielt die Staffel die
 * Schrift sonst fest, während die Fläche schon weg war (Prototyp, Fix 5).
 */
export const SELECTION_HULL_STAGGER = 0.03

/**
 * Warum die Hülle neu steht: eine andere Auswahl (fährt), dieselbe per Pfeiltaste (fährt kürzer) oder
 * nur ein anderes Layout (springt).
 */
export type SelectionHullCause = 'selection' | 'key' | 'layout'

/**
 * @example
 * <motion.div animate={{ top, height }} transition={selectionHullTransition('selection')} />
 */
export function selectionHullTransition(cause: SelectionHullCause, reduced = false): Transition {
  if (cause === 'layout' || reduced) return { duration: 0 }
  return { duration: cause === 'key' ? SELECTION_HULL_KEY_DURATION : SELECTION_HULL_DURATION, ease: SELECTION_HULL_EASE }
}

/**
 * Verzug in Millisekunden für den Rang einer dazukommenden Zeile (0 = die erste, ohne Verzug).
 *
 * @example
 * row.style.setProperty('--selection-delay', `${selectionHullDelay(rank)}ms`)
 */
export function selectionHullDelay(rank: number, reduced = false): number {
  if (reduced || rank <= 0) return 0
  return Math.round(rank * SELECTION_HULL_STAGGER * 1000)
}
